import { List, Map, Record, getIn } from 'immutable';
import { Utils } from '@kineticdata/bundle-common';
import isobject from 'isobject';
import isarray from 'isarray';
const { noPayload, withPayload } = Utils;
const ns = Utils.namespaceBuilder('settings/adminForms');

export const FORMS_INCLUDES = 'details,attributes';
export const FORM_INCLUDES =
  'details,fields,fields.details,attributesMap,categorizations';
export const FORM_FULL_INCLUDES =
  'details,fields,fields.details,bridgedResources,customHeadContent,pages,securityPolicies,attributesMap,categorizations';
export const SUBMISSION_INCLUDES =
  'details,values,form,form.kapp,form.fields,form.fields.details,activities,activities.details';

const PAGE_SIZE = 25;

// Used to define form configurations
export const FormConfig = Record({
  // columns config
  columns: List(),
});

// Used to define a single table column
export const ColumnConfig = Record({
  // name of the column
  name: '',
  // label of the column displayed in table
  label: '',
  // Valid types are: system, value.
  type: '',
  // if the column is displayable in the table
  visible: false,
  // if the column can be filtered
  filterable: false,
  // if the column can be sorted
  sortable: false,
  dataType: 'string',
});

export const SUBMISSION_SYSTEM_PROPS = [
  ColumnConfig({
    label: 'Handle',
    name: 'handle',
    type: 'system',
    visible: true,
    filterable: true,
    sortable: true,
  }),
  ColumnConfig({
    label: 'Label',
    name: 'label',
    type: 'system',
    visible: true,
    filterable: true,
    sortable: true,
  }),
  ColumnConfig({ label: 'Created At', name: 'createdAt', type: 'system' }),
  ColumnConfig({ label: 'Created By', name: 'createdBy', type: 'system' }),
  ColumnConfig({ label: 'Updated At', name: 'updatedAt', type: 'system' }),
  ColumnConfig({ label: 'Updated By', name: 'updatedBy', type: 'system' }),
  ColumnConfig({ label: 'Id', name: 'id', type: 'system' }),
];

export const parseFormConfigurationJson = json => {
  try {
    const parsed = JSON.parse(json);
    if (isobject(parsed)) {
      return FormConfig(parsed).update('columns', c => List(c));
    } else if (isarray(parsed)) {
      return FormConfig({
        columns: [
          ...parsed.map(c => ({
            label: c.title,
            name: c.data,
            type: 'value',
            visible: c.visible,
            filterable: c.searchable,
            sortable: c.orderable,
            ...c,
          })),
          ...(parsed.length > 0
            ? [
                {
                  label: 'Handle',
                  name: 'handle',
                  type: 'system',
                },
                {
                  label: 'Label',
                  name: 'label',
                  type: 'system',
                },
              ]
            : []),
        ],
      }).update('columns', c => List(c));
    } else {
      return FormConfig();
    }
  } catch (e) {
    return FormConfig();
  }
};

export const buildFormConfigurationObject = form => {
  // Parse Form Attribute for Configuration Values
  const parsedConfig = parseFormConfigurationJson(
    form.attributesMap['Datastore Configuration'] &&
      form.attributesMap['Datastore Configuration'][0],
  );
  // Build a list of all current column properties
  let defaultColumnConfig = List(
    form.fields
      .map(f =>
        ColumnConfig({
          name: f.name,
          label: f.name,
          type: 'value',
          dataType: f.dataType,
        }),
      )
      .concat(SUBMISSION_SYSTEM_PROPS),
  ).sort((a, b) => {
    var indexA = parsedConfig.columns.findIndex(
      sc => sc.name === a.name && sc.type === a.type,
    );
    var indexB = parsedConfig.columns.findIndex(
      sc => sc.name === b.name && sc.type === b.type,
    );
    if (indexA === indexB) {
      return 0;
    } else if (indexA >= 0 && (indexA < indexB || indexB === -1)) {
      return -1;
    } else {
      return 1;
    }
  });
  // If there are saved column configs, apply them
  if (parsedConfig.columns.size > 0) {
    return parsedConfig.merge({
      columns: List(
        defaultColumnConfig.map(dc => {
          const saved = parsedConfig.columns.find(
            sc => sc.name === dc.name && sc.type === dc.type,
          );
          if (saved) {
            return ColumnConfig({
              ...saved,
              name: dc.name,
              type: dc.type,
              label: dc.label,
              dataType: dc.dataType,
            });
          } else {
            return dc;
          }
        }),
      ),
    });
  } else {
    return parsedConfig.merge({
      columns: List(defaultColumnConfig),
    });
  }
};

export const types = {
  FETCH_FORM_REQUEST: ns('FETCH_FORM_REQUEST'),
  FETCH_FORM_SUCCESS: ns('FETCH_FORM_SUCCESS'),
  FETCH_FORM_FAILURE: ns('FETCH_FORM_FAILURE'),
  FETCH_SUBMISSION_REQUEST: ns('FETCH_SUBMISSION_REQUEST'),
  FETCH_SUBMISSION_SUCCESS: ns('FETCH_SUBMISSION_SUCCESS'),
  FETCH_SUBMISSION_FAILURE: ns('FETCH_SUBMISSION_FAILURE'),
  FETCH_CLONE_SUBMISSION_REQUEST: ns('FETCH_CLONE_SUBMISSION_REQUEST'),
  FETCH_CLONE_SUBMISSION_SUCCESS: ns('FETCH_CLONE_SUBMISSION_SUCCESS'),
  FETCH_CLONE_SUBMISSION_FAILURE: ns('FETCH_CLONE_SUBMISSION_FAILURE'),
  DELETE_FORM_REQUEST: ns('DELETE_FORM_REQUEST'),
  DELETE_FORM_COMPLETE: ns('DELETE_FORM_COMPLETE'),
  CLONE_FORM_REQUEST: ns('CLONE_FORM_REQUEST'),
  CLONE_FORM_COMPLETE: ns('CLONE_FORM_COMPLETE'),
  FETCH_SUBMISSIONS_REQUEST: ns('FETCH_SUBMISSIONS_REQUEST'),
  FETCH_SUBMISSIONS_SUCCESS: ns('FETCH_SUBMISSIONS_SUCCESS'),
  FETCH_SUBMISSIONS_FAILURE: ns('FETCH_SUBMISSIONS_FAILURE'),
  FETCH_SUBMISSIONS_SORT: ns('FETCH_SUBMISSIONS_SORT'),
  FETCH_SUBMISSIONS_FILTER: ns('FETCH_SUBMISSIONS_FILTER'),
  FETCH_SUBMISSIONS_PAGE: ns('FETCH_SUBMISSIONS_PAGE'),
  FETCH_SUBMISSIONS_RESET: ns('FETCH_SUBMISSIONS_RESET'),
  DELETE_SUBMISSION_REQUEST: ns('DELETE_SUBMISSION_REQUEST'),

  EXECUTE_IMPORT: ns('EXECUTE_IMPORT'),
  SET_IMPORT_PERCENT_COMPLETE: ns('SET_IMPORT_PERCENT_COMPLETE'),
  DEBOUNCE_PERCENT_COMPLETE: ns('DEBOUNCE_PERCENT_COMPLETE'),
  SET_IMPORT_FAILED_CALLS: ns('SET_IMPORT_FAILED_CALLS'),
  SET_IMPORT_COMPLETE: ns('SET_IMPORT_COMPLETE'),
  RESET_IMPORT_FAILED_CALL: ns('RESET_IMPORT_FAILED_CALL'),
};

export const actions = {
  fetchFormRequest: withPayload(types.FETCH_FORM_REQUEST),
  fetchFormSuccess: withPayload(types.FETCH_FORM_SUCCESS),
  fetchFormFailure: withPayload(types.FETCH_FORM_FAILURE),
  fetchSubmissionRequest: withPayload(types.FETCH_SUBMISSION_REQUEST),
  fetchSubmissionSuccess: withPayload(types.FETCH_SUBMISSION_SUCCESS),
  fetchSubmissionFailure: withPayload(types.FETCH_SUBMISSION_FAILURE),
  fetchCloneSubmissionRequest: withPayload(
    types.FETCH_CLONE_SUBMISSION_REQUEST,
  ),
  fetchCloneSubmissionSuccess: withPayload(
    types.FETCH_CLONE_SUBMISSION_SUCCESS,
  ),
  fetchCloneSubmissionFailure: withPayload(
    types.FETCH_CLONE_SUBMISSION_FAILURE,
  ),
  deleteFormRequest: withPayload(types.DELETE_FORM_REQUEST),
  deleteFormComplete: withPayload(types.DELETE_FORM_COMPLETE),
  cloneFormRequest: withPayload(types.CLONE_FORM_REQUEST),
  cloneFormComplete: withPayload(types.CLONE_FORM_COMPLETE),

  fetchSubmissionsRequest: withPayload(types.FETCH_SUBMISSIONS_REQUEST),
  fetchSubmissionsSuccess: withPayload(types.FETCH_SUBMISSIONS_SUCCESS),
  fetchSubmissionsFailure: withPayload(types.FETCH_SUBMISSIONS_FAILURE),
  fetchSubmissionsSort: withPayload(types.FETCH_SUBMISSIONS_SORT),
  fetchSubmissionsFilter: withPayload(types.FETCH_SUBMISSIONS_FILTER),
  fetchSubmissionsPage: withPayload(types.FETCH_SUBMISSIONS_PAGE),
  fetchSubmissionsReset: noPayload(types.FETCH_SUBMISSIONS_RESET),
  deleteSubmissionRequest: withPayload(types.DELETE_SUBMISSION_REQUEST),

  executeImport: withPayload(types.EXECUTE_IMPORT),
  setImportPercentComplete: withPayload(types.SET_IMPORT_PERCENT_COMPLETE),
  debouncePercentComplete: withPayload(types.DEBOUNCE_PERCENT_COMPLETE),
  setImportFailedCalls: withPayload(types.SET_IMPORT_FAILED_CALLS),
  setImportComplete: noPayload(types.SET_IMPORT_COMPLETE),
  resetImportFailedCall: noPayload(types.RESET_IMPORT_FAILED_CALL),
};

const processSubmissions = (state, submissions, { sort, query, page } = {}) => {
  const columns = buildFormConfigurationObject(state.get('form')).get(
    'columns',
  );
  const filterableColumns = columns.filter(c => c.filterable);

  // Get data from correct source
  const data =
    submissions ||
    (typeof query === 'undefined'
      ? state.getIn(['submissionsFiltering', 'data'])
      : state.get('submissions'));

  // Define filter by, sort direction, and sort by values
  const filterBy = Array.isArray(query)
    ? query
    : state.getIn(['submissionsFiltering', 'query']);
  const isFiltered =
    Array.isArray(filterBy) && filterBy.some(q => q && q.length > 0);
  const sortDirection =
    sort && state.getIn(['submissionsFiltering', 'sort']) === sort
      ? state.getIn(['submissionsFiltering', 'direction']) * -1
      : state.getIn(['submissionsFiltering', 'direction']) || 1;
  let sortBy = sort || state.getIn(['submissionsFiltering', 'sort']);
  if (!sortBy) {
    const column = columns.find(c => c.sortable) || columns.first();
    sortBy = column.type === 'value' ? `values.${column.name}` : column.name;
  }

  // Filter data
  const filteredData = isFiltered
    ? data.filter(d => {
        return filterBy
          .filter(Boolean)
          .every(filterValue =>
            filterableColumns.some(
              c =>
                c.type === 'value'
                  ? Array.isArray(d['values'][c.name])
                    ? d['values'][c.name].some(v =>
                        (v || '')
                          .toLowerCase()
                          .includes(filterValue.toLowerCase()),
                      )
                    : (d['values'][c.name] || '')
                        .toLowerCase()
                        .includes(filterValue.toLowerCase())
                  : (d[c.name] || '')
                      .toLowerCase()
                      .includes(filterValue.toLowerCase()),
            ),
          );
      })
    : data;

  // Sort data
  const sortedData =
    sort || isFiltered || submissions
      ? filteredData.sortBy(
          s => getIn(s, sortBy.split('.')),
          (a, b) =>
            (a === b
              ? 0
              : a === null || a === undefined
                ? -1
                : a.localeCompare(b)) * sortDirection,
        )
      : filteredData;

  // Define paging variables
  const maxPage = Math.ceil(sortedData.size / PAGE_SIZE);
  const currentPage = Math.max(
    Math.min(
      page ||
        (filterBy !== null ? 1 : state.getIn(['submissionsPaging', 'page'])),
      maxPage,
    ),
    1,
  );

  // Get page of data
  const pagingData = sortedData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const newState = submissions ? state.set('submissions', submissions) : state;
  return newState
    .set('submissionsFiltering', {
      query:
        filterBy !== null
          ? filterBy || []
          : state.getIn(['submissionsFiltering', 'query']),
      sort: sortBy,
      direction: sortDirection,
      data: sortedData,
      isFiltered,
    })
    .set('submissionsPaging', {
      page: currentPage,
      maxPage,
      data: pagingData,
      count: sortedData.size,
      pageSize: PAGE_SIZE,
    });
};

export const State = Record({
  processing: Map(),
  form: null,
  error: null,
  submission: null,
  submissionError: null,
  cloneSubmission: null,
  cloneSubmissionError: null,
  submissions: null,
  submissionsError: null,
  submissionsFiltering: { query: [], sort: null, direction: 1, data: null },
  submissionsPaging: {
    page: 1,
    maxPage: 0,
    data: List(),
    count: 0,
    pageSize: PAGE_SIZE,
  },

  importProcessing: false,
  importPercentComplete: 0,
  importFailedCalls: List(),
  importComplete: false,
});

export const reducer = (state = State(), { type, payload }) => {
  switch (type) {
    case types.FETCH_FORM_REQUEST:
      return state
        .update(
          'form',
          form => (form && form.slug === payload.formSlug ? form : null),
        )
        .set('error', null);
    case types.FETCH_FORM_SUCCESS:
      return state.set('form', payload);
    case types.FETCH_FORM_FAILURE:
      return state.set('error', payload);
    case types.FETCH_SUBMISSION_REQUEST:
      return state
        .update(
          'submission',
          submission =>
            submission && submission.id === payload.id ? submission : null,
        )
        .set('submissionError', null);
    case types.FETCH_SUBMISSION_SUCCESS:
      return state.set('submission', payload);
    case types.FETCH_SUBMISSION_FAILURE:
      return state.set('submissionError', payload);
    case types.FETCH_CLONE_SUBMISSION_REQUEST:
      return state
        .update(
          'cloneSubmission',
          submission =>
            submission && submission.id === payload.id ? submission : null,
        )
        .set('cloneSubmissionError', null);
    case types.FETCH_CLONE_SUBMISSION_SUCCESS:
      return state.set('cloneSubmission', payload);
    case types.FETCH_CLONE_SUBMISSION_FAILURE:
      return state.set('submissionError', payload);
    case types.DELETE_FORM_REQUEST:
      return state.setIn(['processing', payload.formSlug], true);
    case types.DELETE_FORM_COMPLETE:
      return state.deleteIn(['processing', payload.formSlug]);
    case types.CLONE_FORM_REQUEST:
      return state.setIn(['processing', payload.cloneFormSlug], true);
    case types.CLONE_FORM_COMPLETE:
      return state.deleteIn(['processing', payload.cloneFormSlug]);

    case types.FETCH_SUBMISSIONS_REQUEST:
      return state.set('submissionsError', null);
    case types.FETCH_SUBMISSIONS_SUCCESS:
      return processSubmissions(state, List(payload));
    case types.FETCH_SUBMISSIONS_FAILURE:
      return state.set('submissionsError', payload);
    case types.FETCH_SUBMISSIONS_RESET:
      return state
        .set('submissions', null)
        .set('submissionsFiltering', {
          query: [],
          sort: null,
          direction: 1,
          data: null,
        })
        .set('submissionsPaging', {
          page: 1,
          data: List(),
        });
    case types.FETCH_SUBMISSIONS_SORT:
      return processSubmissions(state, null, { sort: payload });
    case types.FETCH_SUBMISSIONS_FILTER:
      return processSubmissions(state, null, { query: payload });
    case types.FETCH_SUBMISSIONS_PAGE:
      return processSubmissions(state, null, { page: payload });

    case types.EXECUTE_IMPORT:
      return state.set('importProcessing', true).set('importComplete', false);
    case types.SET_IMPORT_PERCENT_COMPLETE:
      return state.set('importPercentComplete', payload);
    case types.SET_IMPORT_FAILED_CALLS:
      return state.update('importFailedCalls', acc => {
        if (Array.isArray(payload)) {
          return acc.concat(payload);
        } else {
          return acc.push(payload);
        }
      });
    case types.SET_IMPORT_COMPLETE:
      return state
        .set('importProcessing', false)
        .set('importPercentComplete', 0)
        .set('importComplete', true);
    case types.RESET_IMPORT_FAILED_CALL:
      return state.set('importFailedCalls', List());

    default:
      return state;
  }
};

export default reducer;
